import React, { useState } from "react";
import { X, Copy, AlertTriangle, FileDown, Tag } from "lucide-react";
import { Customer } from "../../../types/customer";
import { BanRequestModal } from "../../banRequest/BanRequestModel";
import { BanOptionsModal } from "../../banRequest/BanOptionModel";

interface CustomerDetailsDrawerProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (customer: Customer) => void;
  onDelete?: (customerId: string) => void;
  lastEntry?: string | undefined;
}

export const CustomerDetailsDrawer: React.FC<CustomerDetailsDrawerProps> = ({
  customer,
  isOpen,
  onClose,
  lastEntry,
}) => {
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [banOptionsModalOpen, setBanOptionsModalOpen] = useState(false);

  if (!customer) return null;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleUnderMonitoringClick = () => {
    setBanModalOpen(true);
  };

  const handleBlacklistClick = () => {
    setBanOptionsModalOpen(true);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] md:w-[500px] bg-slate-800 shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-yellow-400">
            Player Details
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors ml-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto h-full pb-20 pt-6">
          <div className="mb-6 mx-4">
            <div className="bg-slate-700/50 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-lg font-semibold text-white">
                  Personal Information
                </h4>
              </div>

              {/* Two column layout for customer info */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                {/* Left Column */}
                <div className="space-y-3">
                  <div>
                    <div className="text-slate-400 mb-1">Customer ID</div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-mono">
                        #{customer.id.slice(-4)}
                      </span>
                      <button
                        onClick={() => copyToClipboard(customer.id)}
                        className="text-slate-400 hover:text-white transition-colors"
                        aria-label="Copy customer id"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="text-slate-400 mb-1">Full Name</div>
                    <div className="text-white">{customer.name}</div>
                  </div>

                  <div>
                    <div className="text-slate-400 mb-1">Email</div>
                    <div className="flex items-center gap-2">
                      <span className="text-white truncate">
                        {customer.email}
                      </span>
                      <button
                        onClick={() => copyToClipboard(customer.email)}
                        className="text-slate-400 hover:text-white transition-colors flex-shrink-0"
                        aria-label="Copy email"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {customer.phone && (
                    <div>
                      <div className="text-slate-400 mb-1">Phone Number</div>
                      <div className="text-white">{customer.phone}</div>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-3">
                  <div>
                    <div className="text-slate-400 mb-1">Nationality</div>
                    <div className="text-white">
                      {customer.country || "Unknown"}
                    </div>
                  </div>

                  <div>
                    <div className="text-slate-400 mb-1">Registration Date</div>
                    <div className="text-white">
                      {customer.registrationDate
                        ? new Date(
                            customer.registrationDate,
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "-"}
                    </div>
                  </div>

                  <div>
                    <div className="text-slate-400 mb-1">Last Active</div>
                    <div className="text-white">
                      {lastEntry
                        ? new Date(lastEntry).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </div>
                  </div>

                  <div>
                    <div className="text-slate-400 mb-1">Player ID</div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-mono">
                        {customer.playerId}
                      </span>
                      <button
                        onClick={() => copyToClipboard(customer.playerId)}
                        className="text-slate-400 hover:text-white transition-colors"
                        aria-label="Copy player id"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="text-slate-400 mb-1">Royalty</div>
                    <div className="text-white">{customer.royalty}%</div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded text-sm transition-colors">
                  <FileDown className="w-4 h-4" />
                  Export profile to PDF
                </button>
              </div>
            </div>
          </div>

          {/* Actions & Utilities Section */}
          <div className="mb-6 mx-4">
            <h4 className="text-lg font-semibold text-white mb-4">
              Actions & Utilities
            </h4>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleUnderMonitoringClick}
                className="flex items-center gap-2 text-left p-3 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
              >
                <Tag className="w-4 h-4 text-yellow-400" />
                <span className="text-white text-sm">Keep Under Monitor</span>
              </button>

              <button
                onClick={handleBlacklistClick}
                className="flex items-center gap-2 text-left p-3 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
              >
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-white text-sm">Blacklist Account</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Under Monitoring Ban Request Modal */}
      <BanRequestModal
        customer={customer}
        isOpen={banModalOpen}
        onClose={() => setBanModalOpen(false)}
        banType="under_monitoring"
      />

      {/* Blacklist Options Modal */}
      <BanOptionsModal
        customer={customer}
        isOpen={banOptionsModalOpen}
        onClose={() => setBanOptionsModalOpen(false)}
      />
    </>
  );
};
